import { useState } from 'react';
import { Award, CheckCircle2, Save, ShieldCheck, PhoneCall, FileText } from 'lucide-react';
import { useStore } from '@/store/StoreContext';
import { Card, SectionTitle, EmptyState, Button } from '@/components/ui';

const ASSESSMENT_MODULES = [
  'Direct Debit + OAM Promotion',
  'Customer Contact',
  'Meter Location Request',
  'Move In / Out',
  'Meter Reading & Billing',
  'CRU & Complaints',
];

export function AssessmentsView() {
  const { activeClass, activeClassAgents, updateAgentAssessment, userRole } = useStore();
  const [selectedAgentId, setSelectedAgentId] = useState(activeClassAgents[0]?.id || '');
  const [saved, setSaved] = useState(false);

  const selectedAgent = activeClassAgents.find((a) => a.id === selectedAgentId);

  const handleAssessmentChange = (key: string, value: any) => {
    if (userRole === 'viewer' || userRole === 'guest') {
      alert('Viewers have read-only access.');
      return;
    }
    if (!selectedAgentId) return;
    updateAgentAssessment(selectedAgentId, key, value);
  };

  const handleSave = () => {
    if (userRole === 'viewer' || userRole === 'guest') {
      alert('Viewers have read-only access.');
      return;
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <SectionTitle
        title="Assessments & Accreditation"
        subtitle="Track core module scores (85% pass threshold), written assessment attempts, and call accreditation."
        icon={<Award className="w-6 h-6" />}
      />

      {!activeClass || activeClassAgents.length === 0 ? (
        <Card className="p-12 text-center text-slate-500">
          <EmptyState message="No active class or trainees found. Please add trainees to the roster first." />
        </Card>
      ) : (
        <div className="space-y-6">
          <Card className="p-6">
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Select Trainee</label>
            <select
              value={selectedAgentId}
              onChange={(e) => setSelectedAgentId(e.target.value)}
              className="w-full md:w-80 rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 bg-white font-medium focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            >
              {activeClassAgents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name} ({agent.pNumber})
                </option>
              ))}
            </select>
          </Card>

          {selectedAgent && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Module Assessments */}
              <Card className="p-6 space-y-5">
                <div className="flex items-center gap-2.5 border-b border-slate-100 pb-3">
                  <ShieldCheck className="w-5 h-5 text-primary-600" />
                  <h3 className="text-base font-bold text-slate-900">Module Score Tracker (85% Pass Mark)</h3>
                </div>

                <div className="space-y-3">
                  {ASSESSMENT_MODULES.map((modName) => {
                    const scoreVal = (selectedAgent.assessments as any)?.[modName] ?? '';
                    const numericScore = parseFloat(scoreVal);
                    const hasScore = !isNaN(numericScore) && scoreVal !== '';
                    const isPass = hasScore && numericScore >= 85;

                    return (
                      <div key={modName} className="p-3.5 rounded-xl bg-slate-50/80 border border-slate-100 flex items-center justify-between gap-4">
                        <span className="font-semibold text-slate-800 text-xs flex-1">{modName}</span>

                        <div className="flex items-center gap-2 shrink-0">
                          <div className="relative w-24">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              placeholder="Score"
                              value={scoreVal}
                              onChange={(e) => handleAssessmentChange(modName, e.target.value)}
                              className="w-full rounded-lg border border-slate-300 pr-6 pl-2.5 py-1.5 text-xs text-right font-bold text-slate-900 focus:border-primary-500 focus:outline-none"
                            />
                            <span className="absolute right-2 top-1.5 text-xs text-slate-400 font-bold">%</span>
                          </div>

                          <span className={`w-16 py-1 rounded-lg text-[11px] font-bold text-center border ${
                            !hasScore
                              ? 'bg-slate-100 text-slate-400 border-slate-200'
                              : isPass
                              ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                              : 'bg-red-100 text-red-800 border-red-300'
                          }`}>
                            {!hasScore ? 'N/A' : isPass ? 'Pass' : 'Fail'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* Final Assessments Pipeline */}
              <div className="space-y-6">
                {/* Written Final Assessment */}
                <Card className="p-6 space-y-4">
                  <div className="flex items-center gap-2.5 border-b border-slate-100 pb-3">
                    <FileText className="w-5 h-5 text-primary-600" />
                    <h3 className="text-base font-bold text-slate-900">Written Final Assessment (2 Attempts)</h3>
                  </div>

                  {[1, 2].map((attemptNum) => {
                    const key = `writtenAttempt${attemptNum}`;
                    const val = (selectedAgent.assessments as any)?.[key] ?? '';
                    const numericScore = parseFloat(val);
                    const hasScore = !isNaN(numericScore) && val !== '';
                    const isPass = hasScore && numericScore >= 85;

                    return (
                      <div key={key} className="p-3.5 rounded-xl bg-slate-50/80 border border-slate-100 flex items-center justify-between gap-3">
                        <div>
                          <div className="font-bold text-slate-800 text-xs">Written Attempt {attemptNum}</div>
                          <div className="text-[11px] text-slate-400">Requires 85% or higher</div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <div className="relative w-24">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              placeholder="Score"
                              value={val}
                              onChange={(e) => handleAssessmentChange(key, e.target.value)}
                              className="w-full rounded-lg border border-slate-300 pr-6 pl-2.5 py-1.5 text-xs text-right font-bold text-slate-900 focus:border-primary-500 focus:outline-none"
                            />
                            <span className="absolute right-2 top-1.5 text-xs text-slate-400 font-bold">%</span>
                          </div>

                          <span className={`w-16 py-1 rounded-lg text-[11px] font-bold text-center border ${
                            !hasScore
                              ? 'bg-slate-100 text-slate-400 border-slate-200'
                              : isPass
                              ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                              : 'bg-red-100 text-red-800 border-red-300'
                          }`}>
                            {!hasScore ? 'N/A' : isPass ? 'Pass' : 'Fail'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </Card>

                {/* Call Accreditation */}
                <Card className="p-6 space-y-4">
                  <div className="flex items-center gap-2.5 border-b border-slate-100 pb-3">
                    <PhoneCall className="w-5 h-5 text-primary-600" />
                    <h3 className="text-base font-bold text-slate-900">Call Accreditation (3 Attempts)</h3>
                  </div>

                  {[1, 2, 3].map((attemptNum) => {
                    const key = `callAttempt${attemptNum}`;
                    const val = (selectedAgent.assessments as any)?.[key] ?? '';
                    const numericScore = parseFloat(val);
                    const hasScore = !isNaN(numericScore) && val !== '';
                    const isPass = hasScore && numericScore >= 85;

                    return (
                      <div key={key} className="p-3 rounded-xl bg-slate-50/80 border border-slate-100 flex items-center justify-between gap-3">
                        <div>
                          <div className="font-bold text-slate-800 text-xs">Call Attempt {attemptNum}</div>
                          <div className="text-[11px] text-slate-400">Requires 85% or higher</div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <div className="relative w-24">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              placeholder="Score"
                              value={val}
                              onChange={(e) => handleAssessmentChange(key, e.target.value)}
                              className="w-full rounded-lg border border-slate-300 pr-6 pl-2.5 py-1.5 text-xs text-right font-bold text-slate-900 focus:border-primary-500 focus:outline-none"
                            />
                            <span className="absolute right-2 top-1.5 text-xs text-slate-400 font-bold">%</span>
                          </div>

                          <span className={`w-16 py-1 rounded-lg text-[11px] font-bold text-center border ${
                            !hasScore
                              ? 'bg-slate-100 text-slate-400 border-slate-200'
                              : isPass
                              ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                              : 'bg-red-100 text-red-800 border-red-300'
                          }`}>
                            {!hasScore ? 'N/A' : isPass ? 'Pass' : 'Fail'}
                          </span>
                        </div>
                      </div>
                    );
                  })}

                  <div className="p-3.5 rounded-xl bg-blue-50/60 border border-blue-100 space-y-1.5 mt-2">
                    <label className="block text-xs font-bold text-blue-900 uppercase tracking-wider">Final Accreditation Decision</label>
                    <select
                      value={selectedAgent.assessments.callAccreditation?.finalStatus || 'Pending'}
                      onChange={(e) => handleAssessmentChange('callAccreditation.finalStatus', e.target.value)}
                      className="w-full rounded-lg border border-blue-200 px-3 py-2 text-xs font-bold text-blue-950 bg-white focus:border-primary-500 focus:outline-none cursor-pointer"
                    >
                      <option value="Pending">Pending Evaluation</option>
                      <option value="Pass">Fully Accredited (Pass)</option>
                      <option value="Fail">Not Accredited (Fail)</option>
                    </select>
                  </div>
                </Card>
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            {saved && (
              <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 animate-fade-in">
                <CheckCircle2 className="h-4 w-4" /> Scores saved successfully!
              </span>
            )}
            <Button onClick={handleSave}>
              <Save className="h-4 w-4" /> Save Assessments
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
